'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

/** Ver contracts/infinite-list-hook.md (specs/012-lo-mas-vendido-listado). */
export type InfiniteListStatus = 'idle' | 'loading' | 'error'

/**
 * Configuración de `useInfiniteList`. Deliberadamente sin valores por defecto:
 * `pageSize`, `threshold` y `getItemKey` los declara cada página que integra el
 * hook, porque cada API paginada puede tener un tamaño de página distinto
 * (FR-016) — este hook no conoce el tipo de entidad ni la API concreta.
 */
export interface UseInfiniteListConfig<TItem> {
  /** Ítems ya cargados de la página inicial (obtenida server-side). */
  readonly initialItems: TItem[]
  /** Página con la que arrancó la navegación (1 si no venía `?page=` en la URL). */
  readonly initialPage: number
  /** Total de páginas disponibles, tal como lo devolvió la carga inicial. */
  readonly totalPages: number
  /** Tamaño de página real de la API que consume esta integración (documental —
   * ver contracts/infinite-list-hook.md; no se usa en el cálculo interno del
   * cursor, que trabaja con números de página absolutos). */
  readonly pageSize: number
  /** `rootMargin` del `IntersectionObserver` del sentinela. */
  readonly threshold: string
  /** Clave de deduplicación e identidad de cada ítem. */
  readonly getItemKey: (item: TItem) => string
  /** Adaptador inyectado: el hook no sabe qué API llama ni la forma de `TItem`. */
  readonly fetchPage: (page: number) => Promise<{ items: TItem[]; totalPages: number }>
  /**
   * Se invoca una vez por cada página cargada POR SCROLL (nunca por la inicial,
   * ni por el reset ante una navegación real). Pensado para el `view_more` de
   * analítica, que necesita el acumulado — de ahí `itemsLoadedTotal`, ya
   * deduplicado, en vez del tamaño de la página recién añadida. Opcional: los
   * consumidores que no lo pasan no cambian de comportamiento.
   */
  readonly onPageLoaded?: (info: InfiniteListPageLoadedInfo) => void
}

export interface InfiniteListPageLoadedInfo {
  /** Número de la página que se acaba de cargar. */
  readonly page: number
  /** Total ACUMULADO de ítems ya montados tras añadir esta página. */
  readonly itemsLoadedTotal: number
}

export interface UseInfiniteListResult<TItem> {
  readonly items: TItem[]
  readonly status: InfiniteListStatus
  readonly hasMore: boolean
  /** Ref callback a asignar al elemento centinela al final del listado. */
  readonly sentinelRef: (node: Element | null) => void
  /** Reintenta la última página fallida sin descartar `items`. */
  readonly retry: () => void
  /** Última página cargada con éxito (arranca en `initialPage`). El hook no
   * construye URLs (ver contracts/infinite-list-hook.md) — este número es lo
   * que la página consumidora necesita para reflejar el scroll en `?page=`
   * por su cuenta, sin que el hook sepa que existe una URL. */
  readonly currentPage: number
}

/**
 * Construye la secuencia de páginas a solicitar tras la inicial: 1..totalPages
 * en orden, saltando `initialPage` (ya mostrada al arrancar). Si `initialPage`
 * es 1, la secuencia es simplemente 2, 3, ..., totalPages (FR-004/FR-012).
 */
function buildPageQueue(initialPage: number, totalPages: number): number[] {
  const queue: number[] = []
  for (let page = 1; page <= totalPages; page += 1) {
    if (page !== initialPage) queue.push(page)
  }
  return queue
}

/** `true` si el foco está en un elemento posterior a `node` en el documento
 * (con el ratón el foco suele quedarse en `body`, que no cuenta). */
function isFocusAfter(node: Element): boolean {
  const active = document.activeElement
  if (!active || active === document.body) return false
  return Boolean(node.compareDocumentPosition(active) & Node.DOCUMENT_POSITION_FOLLOWING)
}

/**
 * Hook genérico de paginación + scroll infinito (FR-005 a FR-012), reutilizable
 * por cualquier listado paginado — no conoce "evento" ni `/data` (FR-015).
 *
 * El cursor de paginación vive en estado (`fetchedCount`), no en refs mutadas
 * durante el render: los refs (`seenKeysRef`, `isLoadingRef`, `observerRef`)
 * solo se leen/escriben dentro de callbacks (promesas, el propio
 * `IntersectionObserver`), nunca en el cuerpo del componente.
 */
export function useInfiniteList<TItem>({
  initialItems,
  initialPage,
  totalPages,
  threshold,
  getItemKey,
  fetchPage,
  onPageLoaded,
}: UseInfiniteListConfig<TItem>): UseInfiniteListResult<TItem> {
  const [items, setItems] = useState<TItem[]>(initialItems)
  const [status, setStatus] = useState<InfiniteListStatus>('idle')
  const [fetchedCount, setFetchedCount] = useState(0)
  const [currentPage, setCurrentPage] = useState(initialPage)
  // Estado, no `useMemo`: la cola solo debe recalcularse cuando el efecto de
  // reset de más abajo decide que hay una navegación real a otra página, NO
  // en cada cambio de `initialPage` (incluido el eco de nuestro propio
  // `router.replace` — ver ese efecto). Si dependiera de `initialPage`
  // directamente (como antes), el eco recalculaba la cola saltándose una
  // página distinta de la original, mientras `fetchedCount` seguía indexando
  // la cola vieja — el índice pasaba a apuntar a la página equivocada y el
  // scroll infinito se saltaba una página (bug reportado con `?page=3`:
  // tras la página 1, en vez de pedir la 2 pedía la 3 de nuevo).
  const [pageQueue, setPageQueue] = useState(() => buildPageQueue(initialPage, totalPages))
  const hasMore = fetchedCount < pageQueue.length

  const seenKeysRef = useRef<Set<string>>(new Set(initialItems.map(getItemKey)))
  const isLoadingRef = useRef(false)
  const observerRef = useRef<IntersectionObserver | null>(null)
  // Refleja `currentPage` sin ser dependencia reactiva (ver el efecto de reset
  // más abajo, que necesita leer su valor más reciente sin dispararse cada vez
  // que cambia).
  const currentPageRef = useRef(initialPage)
  // Se incrementa en cada reset real (ver más abajo) para que una petición ya
  // en curso de la página descartada no aplique su resultado tarde sobre el
  // estado ya reiniciado.
  const resetEpochRef = useRef(0)
  // Última página ya notificada por `onPageLoaded`. Arranca en `initialPage`
  // para no notificar la carga inicial (que no es una carga por scroll), y se
  // reinicia en el efecto de reset para no notificar tampoco la página de
  // destino de una navegación real.
  const lastNotifiedPageRef = useRef(initialPage)
  // El callback se guarda en un ref (actualizado en un efecto, nunca durante el
  // render) para que el efecto de notificación no dependa de su identidad: un
  // consumidor que pase una función inline lo recrearía en cada render.
  const onPageLoadedRef = useRef(onPageLoaded)

  // Quita el listener de `focusin` que acompaña al observer (ver `sentinelRef`).
  const removeFocusListenerRef = useRef<(() => void) | null>(null)

  const disconnectObserver = useCallback(() => {
    observerRef.current?.disconnect()
    observerRef.current = null
    removeFocusListenerRef.current?.()
    removeFocusListenerRef.current = null
  }, [])

  const appendItems = useCallback(
    (newItems: TItem[]) => {
      setItems((current) => {
        const next = [...current]
        for (const item of newItems) {
          const key = getItemKey(item)
          // Dedupe: un ítem ya visto (misma clave) no se añade de nuevo,
          // aunque la página lo repita (FR-006/SC-007).
          if (seenKeysRef.current.has(key)) continue
          seenKeysRef.current.add(key)
          next.push(item)
        }
        return next
      })
    },
    [getItemKey],
  )

  const loadNextPage = useCallback(() => {
    // Guard de concurrencia: una petición en curso bloquea cualquier otra (FR-007).
    if (isLoadingRef.current) return
    if (fetchedCount >= pageQueue.length) {
      disconnectObserver()
      return
    }

    const page = pageQueue[fetchedCount]
    const epoch = resetEpochRef.current
    isLoadingRef.current = true
    setStatus('loading')

    fetchPage(page)
      .then(({ items: newItems }) => {
        // El listado se reinició (navegación real a otra página, ver efecto de
        // reset) mientras esta petición estaba en curso: su resultado ya no
        // corresponde al estado actual, se descarta.
        if (epoch !== resetEpochRef.current) return
        appendItems(newItems)
        isLoadingRef.current = false
        setStatus('idle')
        setCurrentPage(page)
        currentPageRef.current = page
        setFetchedCount((count) => {
          const next = count + 1
          // Parada: al agotar las páginas no deben quedar listeners activos (FR-008).
          if (next >= pageQueue.length) disconnectObserver()
          return next
        })
      })
      .catch(() => {
        if (epoch !== resetEpochRef.current) return
        isLoadingRef.current = false
        setStatus('error')
      })
  }, [appendItems, fetchPage, disconnectObserver, fetchedCount, pageQueue])

  const retry = useCallback(() => {
    loadNextPage()
  }, [loadNextPage])

  const sentinelRef = useCallback(
    (node: Element | null) => {
      disconnectObserver()
      if (!node) return
      if (fetchedCount >= pageQueue.length) return

      // Con el foco de teclado por debajo del listado (el usuario ha tabulado
      // hasta el pie) no se carga más: la página nueva entraría ENCIMA del pie
      // y dejaría el elemento enfocado fuera de pantalla (WCAG 2.4.7). La
      // carga se retoma al volver el foco al listado o al seguir con scroll.
      let intersecting = false
      const tryLoad = () => {
        if (intersecting && !isFocusAfter(node)) loadNextPage()
      }
      const observer = new IntersectionObserver(
        (entries) => {
          intersecting = entries.some((entry) => entry.isIntersecting)
          tryLoad()
        },
        { rootMargin: threshold },
      )
      observer.observe(node)
      observerRef.current = observer
      document.addEventListener('focusin', tryLoad)
      removeFocusListenerRef.current = () => document.removeEventListener('focusin', tryLoad)
    },
    [threshold, loadNextPage, disconnectObserver, fetchedCount, pageQueue],
  )

  useEffect(() => disconnectObserver, [disconnectObserver])

  useEffect(() => {
    onPageLoadedRef.current = onPageLoaded
  })

  /**
   * Notifica cada página cargada por scroll. Se hace en un efecto (no en el
   * `.then()` de `loadNextPage`) porque `appendItems` deduplica dentro del
   * updater de `setItems`: el acumulado real solo se conoce una vez React ha
   * aplicado ese estado. `currentPage` y los ítems nuevos se actualizan en el
   * mismo lote, así que aquí `items` ya incluye la página recién añadida.
   */
  useEffect(() => {
    if (currentPage === lastNotifiedPageRef.current) return
    lastNotifiedPageRef.current = currentPage
    onPageLoadedRef.current?.({ page: currentPage, itemsLoadedTotal: items.length })
  }, [currentPage, items.length])

  /**
   * `initialPage`/`initialItems` pueden cambiar sin que el componente se
   * remonte (la página consumidora no incluye la página en el `key` que usa
   * para decidir el remount — ver investigación del salto de scroll): tanto
   * cuando el propio scroll infinito refleja `?page=N` en la URL vía
   * `history.replaceState` (Next.js relanza el render del Server Component
   * para ese `searchParams.page`, aunque no sea una navegación real) como
   * cuando el usuario navega de verdad a otra página (p. ej. un enlace de
   * `SeoPaginationLinks`). No se puede distinguir por el tipo de evento; se
   * distingue comparando contra la página que el propio scroll ya alcanzó
   * (`currentPageRef`):
   * - Coincide → es el eco de nuestro propio `replaceState`, no hay nada que
   *   reiniciar (si se reiniciase aquí, se perdería justo lo que el scroll
   *   acababa de acumular — este es el bug original).
   * - No coincide → es una navegación real a otra página distinta de donde
   *   estaba el scroll: se reinicia el listado desde la nueva página.
   */
  useEffect(() => {
    if (initialPage === currentPageRef.current) return

    resetEpochRef.current += 1
    isLoadingRef.current = false
    disconnectObserver()
    seenKeysRef.current = new Set(initialItems.map(getItemKey))
    currentPageRef.current = initialPage
    // La página de destino de una navegación real no es una carga por scroll:
    // sin esto, el efecto de notificación la vería como "página nueva".
    lastNotifiedPageRef.current = initialPage
    setItems(initialItems)
    setStatus('idle')
    setFetchedCount(0)
    setCurrentPage(initialPage)
    setPageQueue(buildPageQueue(initialPage, totalPages))
    // Solo `initialPage` decide si este efecto actúa (comparando su VALOR
    // contra `currentPageRef`, no su identidad). `initialItems`/`getItemKey`
    // se leen aquí mismo (últimos valores recibidos, vía closure) pero no
    // como dependencias: si lo fueran, un consumidor que pase un `getItemKey`
    // inline (como `AgendaResults`/`SearchResults`) o un array recreado en
    // cada render dispararía este efecto en renders donde `initialPage` no
    // cambió de verdad — justo cuando `currentPageRef` ya se había adelantado
    // por el scroll, provocando un reset no deseado (así fallaban los tests
    // que no cambian `initialPage`: cada re-render interno del propio hook
    // volvía a evaluar la condición con un `currentPageRef` ya avanzado).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPage, disconnectObserver])

  return { items, status, hasMore, sentinelRef, retry, currentPage }
}
